import { Reducer, useEffect, useReducer } from "react"
import { useAuth } from "./auth"

import { Review } from "../book/data-types"
import { getBookReviews, getProfileReviews, getSingleReview } from "../book/reviews"


type SearchState = {
    pending: boolean
    final: boolean
    reviews?: Array<Review>
    error?: string
}

type UpdateFunction = (offset: number, limit: number) => Promise<void>

type ReviewState = {
    pending: boolean
    final: boolean
    review?: Review
    error?: string
}

export const useBookReviews = (bookId: number): [SearchState, UpdateFunction] => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<SearchState, Partial<SearchState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            final: true,
            reviews: [],
        }
    )

    const update = async (offset: number, limit: number) => {
        setState({ pending: true, final: false })
        try {
            const reviews = await getBookReviews(bookId, offset, limit, user.token?.access)

            if (!reviews?.length) {
                setState({ final: true, pending: false })
                return
            }

            setState({ reviews: [ ...(state.reviews || []), ...reviews], pending: false })
        } catch (e) {
            setState({ error: e as string, pending: false })
        }
    }

    return [state, update]
}

export const useProfileReviews = (publicId: string): [SearchState, UpdateFunction] => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<SearchState, Partial<SearchState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            final: true,
            reviews: [],
        }
    )

    const update = async (offset: number, limit: number) => {
        setState({ pending: true, final: false })
        try {
            const reviews = await getProfileReviews(publicId, offset, limit, user.token?.access)

            if (!reviews?.length) {
                setState({ final: true, pending: false })
                return
            }

            setState({ reviews: [ ...(state.reviews || []), ...reviews], pending: false })
        } catch (e) {
            setState({ error: e as string, pending: false })
        }
    }

    return [state, update]
}

export const useReview = (reviewId?: number): ReviewState => {
    const { token } = useAuth()

    const [state, setState] = useReducer<Reducer<ReviewState, Partial<ReviewState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            final: true,
            review: undefined,
        }
    )

    useEffect(() => {
        (async() => {
            if (!token?.access) {
                return
            }

            if (!reviewId) {
                return
            }

            setState({ pending: true, error: undefined })

            try {
                const review = await getSingleReview(reviewId, token.access)
                setState({ 
                    pending: false, 
                    review,
                })
            } catch (e) {
                setState({
                    pending: false,
                    error: "Can't fetch your review"
                })
            }
        })()
    }, [reviewId, token?.access])


    return state
}
